import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useNavigate } from 'react-router-dom'

// UI

// Component

import { authSelector } from '@/stores/auth/authSlice'
//Api
import { convertTableStateToQuery } from '@/utils/tableHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable, { tableInitialState } from '@/components/Table/FullTable/FullTable'
import { OfferControlStatus, OfferQuotationStatus } from '@/features/offers/constants'
import useOfferList from '@/features/offers/hooks/useOfferList'
import { LoadingSection } from '@eapproval/ui'

// UI
import { Box } from '@chakra-ui/react'

// Component

//Api
//Slice

//Helper / Constant

import { camelToWords } from '@/utils/stringHelper'

import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import { useReason } from '@/features/reasons/useReason'
import { MENU_SUB_GROUP_NONE, SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import useUser from '@/features/users/useUser'
import { OfferBookingStatus, OfferStage } from '@eapproval/offer-shared'
import { getOfferListColumns } from './offerListColmns'
import OfferListHeader from './OfferListHeader'

const OfferListPage = ({ offerStage, offerType, isEQuotation = false, menuSubGroup }) => {
  const isMenuSubGroupList = Boolean(menuSubGroup)
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()

  const salesResponsible = searchParams.get('salesResponsible')
  const status = searchParams.get('status')

  const initialFilter = useMemo(() => {
    const columnFilters = []

    if (status) {
      columnFilters.push({ id: 'status', value: status.split(',') })
    }

    if (salesResponsible) {
      columnFilters.push({ id: 'salesResponsible', value: salesResponsible })
    }

    return {
      columnFilters: columnFilters,
    }
  }, [status, salesResponsible])

  const tableStateKey = isMenuSubGroupList
    ? `table_state:${offerStage}_menuSubGroup_${menuSubGroup}`
    : `table_state:${offerStage}_${offerType}`
  const [storedTableState, setStoredTableState] = useInitialTableState(tableStateKey, {})
  const query = convertTableStateToQuery(storedTableState)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { useFetchMyOffers } = useOfferList()
  const { useFetchAllUsers, useUserOptions } = useUser()

  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useSystemVariableOptions } = useSystemVariable()

  const { useFetchReasonsByType } = useReason()

  const { data: fetchReasonResponse } = useFetchReasonsByType('offer')

  const allReasons = fetchReasonResponse ?? []

  const closeMainReasons = allReasons.map((item) => item.name)

  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: allUserOptions } = useUserOptions()
  const { data: PMOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, 'sales'],
  })

  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })

  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'simple' })
  const { data: GCKOptions } = useGCKCodeOptions()

  const offerStatusOptions = (
    offerStage === 'quotation'
      ? Object.values({ ...OfferQuotationStatus, ...OfferControlStatus }).concat(closeMainReasons)
      : Object.values({ ...OfferBookingStatus })
  ).map((status) => {
    return {
      label: camelToWords(status),
      value: status,
    }
  })

  const filters = {
    ...query.filters,
  }

  if (isMenuSubGroupList) {
    filters.menuSubGroup = menuSubGroup
  } else {
    filters.mainSubSystemType = offerType
    filters.isEQuotation = isEQuotation ? 'yes' : 'no'
    filters.menuSubGroup = MENU_SUB_GROUP_NONE
  }

  if (offerStage === OfferStage.BOOKING) {
    filters.offerStage = OfferStage.BOOKING
  }

  const { data: offersResponse, isLoading, isRefetching } = useFetchMyOffers(
    {
      ...query,
      filters: filters,
    },
    MyOfferDisplayLevel.OFFER
  )

  const count = offersResponse?.count
  useEffect(() => {
    if (Array.isArray(initialFilter?.columnFilters) && initialFilter?.columnFilters.length > 0) {
      setStoredTableState((prev) => {
        return {
          ...tableInitialState,
          ...prev,
          ...initialFilter,
        }
      })

      // navigate(`${location.pathname}?type=${type}`, { replace: true })
    }
  }, [initialFilter, setStoredTableState])

  //Handler

  const handleGotoOffer = (row) => {
    if (!row?.original?._id) {
      return
    }
    navigate(`/offers/${row?.original?._id}`)
  }

  const defaultColumns = getOfferListColumns({
    offerType: offerType,
    offerStage: offerStage,
    offerStatusOptions: offerStatusOptions,
    salesUserOptions,
    creatorOptions: allUserOptions,
    teamOptions,
    businessUnitOptions,
    PMOptions,
    costCenterOptions,
    GCKOptions,
    currentUser,
  })

  if (!currentUser?.fullName || isLoading) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" w="100%" mb="auto" h="100%">
      <OfferListHeader
        offerStage={offerStage}
        offerType={offerType}
        isEQuotation={isEQuotation}
        menuSubGroup={menuSubGroup}
        dataCount={count}
        filters={filters}
      />

      <Box p={5} h="90%">
        <FullTable
          data={offersResponse?.data ? offersResponse.data : []}
          rowCount={count}
          columnDef={defaultColumns}
          handleRowClick={handleGotoOffer}
          state={storedTableState}
          setState={setStoredTableState}
          isLoading={isRefetching || isLoading}
        />
      </Box>
    </Box>
  )
}

export default OfferListPage
